import { useEffect, useMemo, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import type {
  NotificationCatalogue,
  NotificationEventInfo,
  NotificationTargetType,
} from '@/lib/types';

/*
 * The notification catalogue (`GET /notifications/catalogue`) flattened into
 * the three target levels a rule or a default can name.
 *
 * Target keys, as the server reads them:
 *   module     `quotes`
 *   submodule  `quotes.signatures`   (module key + '.' + submodule key)
 *   event      `quotes.signatures.signed`
 *
 * Shown as `Module › Submodule › Event`.
 */

export interface NotificationTarget {
  type: NotificationTargetType;
  key: string;
  /** `Quotes › Signatures › Quote signed`. */
  path: string;
  /** Indent level: 0 module, 1 submodule, 2 event. */
  depth: 0 | 1 | 2;
  moduleKey: string;
  /** Every event this target covers. */
  events: NotificationEventInfo[];
  /** Every covered event is locked (cannot be switched off). */
  locked: boolean;
  /** At least one covered event is a client event, so a mine/all scope applies. */
  clientScoped: boolean;
}

export const TARGET_SEPARATOR = ' › ';

export function targetId(type: NotificationTargetType, key: string): string {
  return `${type}:${key}`;
}

export function parseTargetId(value: string): { type: NotificationTargetType; key: string } | null {
  const colon = value.indexOf(':');
  if (colon <= 0) return null;
  const type = value.slice(0, colon);
  const key = value.slice(colon + 1);
  if (type !== 'module' && type !== 'submodule' && type !== 'event') return null;
  if (!key) return null;
  return { type, key };
}

function summarise(
  type: NotificationTargetType,
  key: string,
  path: string,
  depth: 0 | 1 | 2,
  moduleKey: string,
  events: NotificationEventInfo[],
): NotificationTarget {
  return {
    type,
    key,
    path,
    depth,
    moduleKey,
    events,
    locked: events.length > 0 && events.every((event) => event.locked),
    clientScoped: events.some((event) => event.clientEvent),
  };
}

/** Catalogue order: each module, then its submodules, each followed by its events. */
export function flattenCatalogue(catalogue: NotificationCatalogue): NotificationTarget[] {
  const out: NotificationTarget[] = [];
  for (const module of catalogue.modules) {
    const moduleEvents = module.submodules.flatMap((submodule) => submodule.events);
    out.push(summarise('module', module.key, module.label, 0, module.key, moduleEvents));
    for (const submodule of module.submodules) {
      const subPath = `${module.label}${TARGET_SEPARATOR}${submodule.label}`;
      out.push(
        summarise(
          'submodule',
          `${module.key}.${submodule.key}`,
          subPath,
          1,
          module.key,
          submodule.events,
        ),
      );
      for (const event of submodule.events) {
        out.push(
          summarise(
            'event',
            event.key,
            `${subPath}${TARGET_SEPARATOR}${event.label}`,
            2,
            module.key,
            [event],
          ),
        );
      }
    }
  }
  return out;
}

export interface CatalogueState {
  catalogue: NotificationCatalogue | null;
  targets: NotificationTarget[];
  byId: Map<string, NotificationTarget>;
  loading: boolean;
  error: string | null;
}

export function useNotificationCatalogue(enabled = true): CatalogueState {
  const [catalogue, setCatalogue] = useState<NotificationCatalogue | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    api
      .getNotificationCatalogue(controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setCatalogue(response.data);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        if (caught instanceof ApiError && caught.status === 401) return;
        setError(caught instanceof Error ? caught.message : 'The notification catalogue could not be read.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [enabled]);

  const targets = useMemo(() => (catalogue ? flattenCatalogue(catalogue) : []), [catalogue]);
  const byId = useMemo(() => {
    const map = new Map<string, NotificationTarget>();
    for (const target of targets) map.set(targetId(target.type, target.key), target);
    return map;
  }, [targets]);

  return { catalogue, targets, byId, loading, error };
}

/** The `Module › Submodule › Event` path, or the raw key for a target the catalogue no longer has. */
export function targetPath(
  byId: Map<string, NotificationTarget>,
  type: NotificationTargetType,
  key: string,
): string {
  return byId.get(targetId(type, key))?.path ?? key;
}

/** A write refusal as one line of text. */
export function errorText(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError) return caught.formMessage ?? caught.message;
  if (caught instanceof Error) return caught.message;
  return fallback;
}
